import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EnvironmentProviders,
  inject,
  input,
  makeEnvironmentProviders,
} from '@angular/core';

import { type MinimalMember, toMemberBrief } from '@core/ask-hr-smart-ui';
import type { AskTemplateViewItem } from '@core/ask-hr-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import type { SymbolEmoji } from '@core/symbol-legacy';
import {
  type ChatPresenterComponent,
  provideTellentAssistantRenderPresenter,
} from '@core/tellent-assistant-contracts';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';

import { ChatFormItemComponent } from '../chat/chat-form-item/chat-form-item.component';

interface RequestFormArgs {
  id: number;
  name: string;
  symbol?: SymbolEmoji | null;
  descriptionJson?: EditorJSONState | null;
  assignees?: MinimalMember[];
}

const EMPTY_DESCRIPTION: EditorJSONState = {
  doc: { type: 'doc', content: [{ type: 'paragraph' }] },
};

@Component({
  selector: 'tellent-assistant-form-renderer',
  imports: [ChatFormItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `,
  template: `<ask-chat-form-item [template]="template()" [redirectUrl]="url()" />`,
})
export class TellentAssistantFormRendererComponent implements ChatPresenterComponent<RequestFormArgs> {
  private readonly buildAppUrl = injectTellentAppUrlBuilder();
  private readonly orgGuid = inject(ORG_GUID, { optional: true });

  protected readonly template = computed<AskTemplateViewItem>(() => {
    const args = this.args();

    return {
      id: args.id,
      name: args.name,
      symbol: args.symbol ?? null,
      descriptionJson: args.descriptionJson ?? EMPTY_DESCRIPTION,
      autoAssignees: (args.assignees ?? []).map(toMemberBrief),
      form: null,
      active: true,
      createdAt: '',
      updatedAt: '',
    };
  });

  protected readonly url = computed(() =>
    this.buildAppUrl(
      AppKind.KiwiHr,
      { id: 'ask:new_ticket', params: { template_id: this.args().id } },
      this.orgGuid ?? undefined,
    ),
  );

  public readonly args = input.required<RequestFormArgs>();
}

export function provideTellentFormCardRenderer(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTellentAssistantRenderPresenter<RequestFormArgs>({
      id: 'presentRequestForm',
      component: TellentAssistantFormRendererComponent,
    }),
  ]);
}
